import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  OnInit,
  Signal,
  signal,
  inject,
} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { Guid, isNotNil, Nillable } from '@web/utils';
import {
  CurrencyComponent,
  DateComponent,
  DateTimeComponent,
  DetailsViewComponent,
  DrawerContentComponent,
  DrawerTitleComponent,
  Icon,
  IconComponent,
  ImageThumbnailComponent,
  NoDataComponent,
  PrimengDividerDirective,
  PrimengDrawerDirective,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { AdminSubscriptionDataSourceService } from '../../services/admin-subscription-data-source.service';
import { AdminSubscriptionDetails } from '../../models/admin-subscription-details';
import { Divider } from 'primeng/divider';
import {
  SubscriptionStatusComponent,
  SubscriptionCancelReasonComponent,
  SubscriptionSuspendReasonComponent,
} from '@web/shared/module/shop';
import { AdminSubscriptionOrderListComponent } from '../admin-subscription-order-list/admin-subscription-order-list.component';
import { AdminSubscriptionOrderEventBusService } from '../../bus/services/admin-subscription-order-event-bus.service';
import { AdminSubscriptionOrderPreviewBusEvent } from '../../bus/events/admin-subscription-order-preview-bus.event';
import { Drawer } from 'primeng/drawer';
import { AdminOrderDetailsComponent } from '../admin-order-details/admin-order-details.component';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { AdminSubscriptionBillingAddressEditComponent } from '../admin-subscription-billing-address-edit/admin-subscription-billing-address-edit.component';
import { AdminSubscriptionShippingAddressEditComponent } from '../admin-subscription-shipping-address-edit/admin-subscription-shipping-address-edit.component';

@Component({
  selector: 'app-admin-subscription-details',
  imports: [
    SpinnerFilledComponent,
    Divider,
    DetailsViewComponent,
    SubscriptionStatusComponent,
    DateComponent,
    DateTimeComponent,
    CurrencyComponent,
    PrimengDividerDirective,
    NoDataComponent,
    ImageThumbnailComponent,
    NgOptimizedImage,
    AdminSubscriptionOrderListComponent,
    Drawer,
    PrimengDrawerDirective,
    DrawerTitleComponent,
    DrawerContentComponent,
    AdminOrderDetailsComponent,
    Button,
    IconComponent,
    Tooltip,
    AdminSubscriptionBillingAddressEditComponent,
    AdminSubscriptionShippingAddressEditComponent,
    SubscriptionCancelReasonComponent,
    SubscriptionSuspendReasonComponent,
  ],
  templateUrl: './admin-subscription-details.component.html',
  styleUrl: './admin-subscription-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminSubscriptionDetailsComponent implements OnInit {
  /* prettier-ignore */
  private readonly subscriptionDataSource = inject(AdminSubscriptionDataSourceService);
  private readonly eventBus = inject(AdminSubscriptionOrderEventBusService);

  public readonly id = input.required<Guid>();

  protected readonly selectedOrderId = signal<Nillable<Guid>>(null);
  protected readonly isNotNil = isNotNil;

  protected readonly data = signal<Nillable<AdminSubscriptionDetails>>(null);
  protected readonly billingAddressEditVisibility = signal<boolean>(false);
  protected readonly shippingAddressEditVisibility = signal<boolean>(false);
  /* prettier-ignore */
  protected selectedOrderDialogVisible = this.selectedOrderDialogVisibleSignal();
  protected readonly Icon = Icon;

  public ngOnInit(): void {
    this.observeBusEvents();

    this.fetchData();
  }

  public handleSubmitBillingAddressEditEvent(): void {
    this.closeBillingAddressEditDialog();
    this.fetchData();
  }

  public handleSubmitShippingAddressEditEvent(): void {
    this.closeShippingAddressEditDialog();
    this.fetchData();
  }

  public handleBillingAddressUpdateClick(): void {
    this.showBillingAddressEditDialog();
  }

  public handleShippingAddressUpdateClick(): void {
    this.showShippingAddressEditDialog();
  }

  protected readonly customCloseOrderDetailsEvent = () => {
    this.selectedOrderId.set(null);
  };

  protected readonly customCloseBillingAddressEditEvent = () => {
    this.closeBillingAddressEditDialog();
  };

  protected readonly customCloseShippingAddressEditEvent = () => {
    this.closeShippingAddressEditDialog();
  };

  private observeBusEvents(): void {
    this.eventBus
      .observe(AdminSubscriptionOrderPreviewBusEvent)
      .subscribe((event) => {
        this.selectedOrderId.set(event.orderId);
      });
  }

  private selectedOrderDialogVisibleSignal(): Signal<boolean> {
    return computed(() => isNotNil(this.selectedOrderId()));
  }

  private closeBillingAddressEditDialog(): void {
    this.billingAddressEditVisibility.set(false);
  }

  private closeShippingAddressEditDialog(): void {
    this.shippingAddressEditVisibility.set(false);
  }

  private showBillingAddressEditDialog(): void {
    this.billingAddressEditVisibility.set(true);
  }

  private showShippingAddressEditDialog(): void {
    this.shippingAddressEditVisibility.set(true);
  }

  private fetchData(): void {
    this.subscriptionDataSource
      .fetchDetailsById(this.id())
      .subscribe((data) => this.data.set(data));
  }
}
